import { useEffect, useRef, useState } from "react";
import {
  CreditCard, Menu, Signal, Wifi, BatteryFull, ChevronLeft, ChevronRight, ChevronDown, CheckCircle2,
  Navigation, UtensilsCrossed,
} from "lucide-react";
import { publicAsset } from "../../../../features/tenantPath";
import Auticko from "../../../../components/Auticko/Auticko";
import { useScenar, usePrelinanie, Pocitadlo, eura } from "../scenar";
import Kapitoly from "./Kapitoly";
import "./UkazkaVodic.scss";

/**
 * Ukazka "Vodic" v sekcii Ako to funguje: mobil s obrazovkou Moje zavozy
 * (DriverRoutesSection + DriverPaymentModal) a vedla neho mapa.
 *
 * Na uzkej obrazovke (mobil) nie su vedla seba -- vidno mobil a pocas jazdy
 * sa prelne na mapu (trieda uv--mapa), po prichode do ciela spat na mobil.
 *
 * Mapa NIE JE sucast aplikacie -- adresa zastavky je v aplikacii odkaz do
 * Google Map (getOrderMapsLink), takze mapa len znazornuje, co sa stane po
 * tuknuti na adresu. Je to vlastna kresba, nie napodobenina Google Map.
 *
 * Vsetko ostatne kopiruje skutocne spravanie:
 *   - zoznam je zoradeny podla stavu (Na trase, Prijata, Dorucena) a kodu adresy,
 *   - "Vybrate z" scituje cenu dorucenych objednavok (nie prijatu hotovost),
 *   - naklad su jedla objednavok Na trase,
 *   - platba sa predvyplni (hotovost, cena objednavky) a hlaska pod suma je
 *     presne ta z PaymentForm (PaymentFeedback),
 *   - dorucena objednavka odide medzi dorucene na koniec zoznamu.
 * Mena, adresy a sumy su vymyslene.
 */

const KROKY = [
  ["start", 0],
  ["dole", 1000],        // vodic prechadza zoznam zastavok
  ["hore", 3000],
  ["tapAdresa", 4800],   // tukne na adresu KOV
  ["navigacia", 5200],   // mapa: cesta z prevadzky ku KOV
  ["ciel", 10800],
  ["spat", 11500],       // na mobile sa z mapy vrati na telefon
  ["tapDorucit", 12300],
  ["platba", 12700],
  ["suma", 14300],       // vodic prepise sumu -- zakaznik vyplati aj dlh
  ["potvrd", 16500],
  ["dorucene", 16900],
  ["dalsia", 18500],     // na mape pokracuje k MRZ
  ["koniec", 24500],     // posledny zaber -- do noveho kola sa prelne
];
const DLZKA_KOLA = 25100;
const JAZDA_MS = 5200;

const KAPITOLY = [
  { nazov: "Zastávky v mobile", krok: "start" },
  { nazov: "Navigácia na adresu", krok: "tapAdresa" },
  { nazov: "Doručenie a platba", krok: "tapDorucit" },
  { nazov: "Ďalšia zastávka", krok: "dorucene" },
];

// cesty na mape (viewBox 360 x 560), po uliciach v pravych uhloch
const CESTA_KOV = "M70 480 L70 390 L190 390 L190 160 L280 160";
const CESTA_MRZ = "M280 160 L320 160 L320 300 L250 300";
const BOD_PREVADZKA = { x: 70, y: 480 };
const BOD_KOV = { x: 280, y: 160 };
const BOD_MRZ = { x: 250, y: 300 };

/** Poloha auta na ceste: pred jazdou na zaciatku, potom ide, po nej v cieli. */
function useJazda(cestaRef, stav, start, ciel) {
  const [poloha, setPoloha] = useState(start);

  useEffect(() => {
    const cesta = cestaRef.current;
    if (stav === "pred") {
      setPoloha(start);
      return undefined;
    }
    if (stav === "ciel" || !cesta?.getTotalLength) {
      setPoloha(ciel);
      return undefined;
    }
    const dlzka = cesta.getTotalLength();
    const t0 = performance.now();
    let raf;
    const snimka = (t) => {
      const p = Math.min(1, (t - t0) / JAZDA_MS);
      // plynuly rozjazd aj dobrzdenie
      const e = p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2;
      const bod = cesta.getPointAtLength(e * dlzka);
      setPoloha({ x: bod.x, y: bod.y });
      if (p < 1) raf = requestAnimationFrame(snimka);
    };
    raf = requestAnimationFrame(snimka);
    return () => cancelAnimationFrame(raf);
    // start a ciel su konstanty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stav, cestaRef]);

  return poloha;
}

const Jedla = ({ polozky }) => (
  <span className="uv__meals">
    {polozky.map(([pocet, kod, zmena]) => (
      <span key={kod} className={zmena ? "uv__meal--zmena" : undefined}>
        {pocet}<b>{kod}</b>
      </span>
    ))}
  </span>
);

const ZASTAVKY_DALSIE = [
  { kod: "MRZ", adresa: "Priemyselná 4, Košice", meno: "Autoservis Mráz", jedla: [["2x", "3"], ["1x", "1"]], cena: "7,60 €" },
  { kod: "PEK", adresa: "Pekárska 21, Košice", meno: "Ing. Pekárová", jedla: [["1x", "1"], ["1x", "2"]], cena: "6,90 €" },
  { kod: "SKO", adresa: "Školská 2, Košice", meno: "ZŠ Školská", jedla: [["2x", "1"], ["1x", "3"]], cena: "8,20 €" },
];
const ZASTAVKY_DORUCENE = [
  { kod: "BOD", adresa: "Bodová 8, Košice", meno: "Kaderníctvo Bella", jedla: [["1x", "1"], ["1x", "2"]], cena: "7,40 €" },
  { kod: "DOM", adresa: "Domová 15, Košice", meno: "Peter Domanský", jedla: [["1x", "3"], ["1x", "2"]], cena: "7,20 €" },
];

/** Zastavka v zozname (DriverOrderCard). */
function Zastavka({ z, stav, saldo, poznamka, telefon, tapAdresa, tapDorucit, className = "" }) {
  return (
    <div className={`uv__stop uv__stop--${stav} ${className}`}>
      <div className="uv__stop-head">
        <span className="uv__code">{z.kod}</span>
        <span className={`uv__addr ${tapAdresa ? "uv__addr--tuk" : ""}`}>
          {z.adresa}
          {tapAdresa !== undefined && <span className={`uv__tap ${tapAdresa ? "uv__tap--tu" : ""}`} />}
        </span>
        <span className={`uv__badge uv__badge--${stav}`}>
          {stav === "na-trase" ? "Na trase" : "Doručená"}
        </span>
      </div>
      <div className="uv__stop-body">
        <div className="uv__who">
          {z.meno} {telefon && <span className="uv__phone-no">{telefon}</span>}
        </div>
        {poznamka && <div className="uv__note">{poznamka}</div>}
        <div className="uv__items"><Jedla polozky={z.jedla} /></div>
        <div className="uv__prices">
          <span>Cena obeda: <b className="uv__price">{z.cena}</b></span>
          <span>Saldo: <b className={saldo ? "uv__saldo--dlh" : ""}>{saldo || "—"}</b></span>
        </div>
      </div>
      {stav === "na-trase" && (
        <div className="uv__deliver">
          <CreditCard size={13} /> Doručiť a zaplatiť
          {tapDorucit !== undefined && <span className={`uv__tap ${tapDorucit ? "uv__tap--tu" : ""}`} />}
        </div>
      )}
      {stav === "dorucena" && className.includes("uv__stop--prave") && (
        <div className="uv__deliver uv__deliver--uprava">
          <CreditCard size={13} /> Upraviť platbu
        </div>
      )}
    </div>
  );
}

/** Mapa vedla mobilu -- len ilustracia navigacie. */
/**
 * `staticky`: predosly zaber pri prelinani -- auto nejazdi, stoji v cieli
 * rozbehnutej cesty (inak by sa jazda v rozplyvajucej sa vrstve spustila znova).
 */
function Mapa({ po, staticky = false, className = "" }) {
  const cestaKovRef = useRef(null);
  const cestaMrzRef = useRef(null);

  const jazda = staticky ? "ciel" : "jazda";
  const jazdaKov = useJazda(cestaKovRef, po("ciel") ? "ciel" : po("navigacia") ? jazda : "pred", BOD_PREVADZKA, BOD_KOV);
  const jazdaMrz = useJazda(cestaMrzRef, po("dalsia") ? jazda : "pred", BOD_KOV, BOD_MRZ);
  const auto = po("dalsia") ? jazdaMrz : jazdaKov;
  const jazdi = (po("navigacia") && !po("ciel")) || po("dalsia");

  const ciel = po("dalsia") ? "MRZ · Priemyselná 4" : "KOV · Hlavná 12";
  const vCieli = po("ciel") && !po("dalsia");

  return (
    <div className={`uv__map ${className}`}>
      <svg viewBox="0 0 360 560" preserveAspectRatio="xMidYMid slice" className="uv__map-svg" aria-hidden="true">
        {/* bloky a zelen */}
        <rect width="360" height="560" className="uv__map-zem" />
        <path d="M0 40 C 90 70, 140 20, 230 50 S 330 90, 360 60 L360 0 L0 0 Z" className="uv__map-voda" />
        <rect x="210" y="410" width="110" height="110" rx="10" className="uv__map-park" />
        {/* ulice */}
        <g className="uv__map-ulice">
          <path d="M20 160 H340" />
          <path d="M20 300 H340" />
          <path d="M20 390 H340" />
          <path d="M20 480 H180" />
          <path d="M70 100 V530" />
          <path d="M190 100 V530" />
          <path d="M320 100 V400" />
        </g>
        <g className="uv__map-nazvy">
          <text x="100" y="153">Hlavná</text>
          <text x="200" y="322">Priemyselná</text>
          <text x="80" y="383">Školská</text>
          <text x="196" y="250" transform="rotate(90 196 250)">Pekárska</text>
          <text x="236" y="470">Park</text>
        </g>

        {/* trasa: po tuknuti na adresu sa vykresli, auto po nej ide */}
        <path
          ref={cestaKovRef}
          d={CESTA_KOV}
          pathLength="1"
          className={`uv__map-cesta ${po("navigacia") && !po("dalsia") ? "uv__map-cesta--tu" : ""}`}
        />
        <path
          ref={cestaMrzRef}
          d={CESTA_MRZ}
          pathLength="1"
          className={`uv__map-cesta ${po("dalsia") ? "uv__map-cesta--tu" : ""}`}
        />

        {/* auto -- autícko z loga Rozvozy; pod znackami, aby ciel nezakrylo */}
        <g transform={`translate(${auto.x} ${auto.y})`} className="uv__map-auto">
          <circle r="15" />
          <g transform="translate(-11 -5)">
            <Auticko sirka={22} rezim={jazdi ? "na-mieste" : "stoji"} />
          </g>
        </g>
        {/* prevadzka a zastavky */}
        <g transform={`translate(${BOD_PREVADZKA.x} ${BOD_PREVADZKA.y})`} className="uv__map-prevadzka">
          <circle r="13" />
          <UtensilsCrossed x="-7" y="-7" size={14} />
        </g>
        {[["KOV", BOD_KOV, !po("dorucene")], ["MRZ", BOD_MRZ, true], ["PEK", { x: 120, y: 300 }, true], ["SKO", { x: 140, y: 390 }, true]].map(([kod, b, aktivny]) => (
          <g
            key={kod}
            transform={`translate(${b.x} ${b.y})`}
            className={[
              "uv__map-pin",
              !aktivny ? "uv__map-pin--hotovo" : "",
              (kod === "KOV" && vCieli) || (kod === "MRZ" && po("dalsia")) ? "uv__map-pin--ciel" : "",
            ].join(" ")}
          >
            <g className="uv__map-pin-tvar">
              <path d="M0 0 C -4 -7, -11 -11, -11 -19 A 11 11 0 1 1 11 -19 C 11 -11, 4 -7, 0 0 Z" />
              <text x="0" y="-15.5">{kod}</text>
            </g>
          </g>
        ))}

      </svg>

      <div className={`uv__map-ciel ${po("navigacia") ? "uv__map-ciel--tu" : ""}`}>
        {vCieli ? <CheckCircle2 size={14} /> : <Navigation size={14} />}
        <span>{vCieli ? "V cieli" : ciel}</span>
      </div>
    </div>
  );
}

/** Obsah obrazovky mobilu v zabere, ktory urcuje `po`. */
function MobilObsah({ po, className = "" }) {
  const dorucene = po("dorucene");
  const platbaOtvorena = po("platba") && !dorucene;
  const posun = po("dole") && !po("hore");

  return (
    <div className={`uv__obsah ${className}`}>
      <div className={`uv__scroll ${posun ? "uv__scroll--dole" : ""}`}>
        <div className="uv__appbar">
          <img src={publicAsset("rozvozy_icon.png")} alt="" />
          <Menu size={16} />
        </div>
        <div className="uv__title">Moje závozy</div>

        <div className="uv__filter">
          <div className="uv__date">
            <ChevronLeft size={12} />
            <span><small>Dátum</small>13.10.2026</span>
            <ChevronRight size={12} />
          </div>
          <div className="uv__route">
            <small>Trasa</small>
            <span><i /> SEVER <ChevronDown size={11} /></span>
          </div>
        </div>

        <div className="uv__stats">
          <div>
            <strong>{dorucene ? 3 : 2} / 6</strong>
            <small>Doručené</small>
          </div>
          <div>
            <strong className="uv__green"><Pocitadlo hodnota={dorucene ? 21.5 : 14.6} format={eura} /></strong>
            <small>Vybraté z 44,20 €</small>
          </div>
          <div className="uv__cargo">
            <small>Aktuálne naložené jedlá</small>
            <Jedla polozky={dorucene
              ? [["4x", "1", true], ["1x", "2", true], ["3x", "3"]]
              : [["5x", "1"], ["3x", "2"], ["3x", "3"]]}
            />
          </div>
        </div>

        {/* KOV: Na trase a kodom adresy prva -- po doruceni odide medzi dorucene */}
        <div className={`uv__fold ${dorucene ? "uv__fold--zatvorena" : ""}`}>
          <div>
            <Zastavka
              z={{ kod: "KOV", adresa: "Hlavná 12, Košice", meno: "Mgr. Kováčová", jedla: [["2x", "2"], ["1x", "1"]], cena: "6,90 €" }}
              stav={dorucene ? "dorucena" : "na-trase"}
              telefon="+421 900 123 456"
              poznamka="nechať na vrátnici"
              saldo="12,40 € (Dlh)"
              tapAdresa={po("tapAdresa") && !po("navigacia")}
              tapDorucit={po("tapDorucit") && !po("platba")}
              className={[
                po("ciel") && !po("platba") ? "uv__stop--zvyraznena" : "",
                dorucene ? "uv__stop--prave" : "",
              ].join(" ")}
            />
          </div>
        </div>

        {ZASTAVKY_DALSIE.map((z) => (
          <Zastavka key={z.kod} z={z} stav="na-trase" />
        ))}
        {ZASTAVKY_DORUCENE.map((z) => (
          <Zastavka key={z.kod} z={z} stav="dorucena" />
        ))}
      </div>

      {/* DriverPaymentModal -- na mobile sa vysuva zospodu */}
      <div className={`uv__sheet ${platbaOtvorena ? "uv__sheet--tu" : ""}`}>
        <div className="uv__sheet-panel">
          <div className="uv__sheet-title">Platba a doručenie</div>
          <div className="uv__info">
            <div><span>Adresa</span><b><span className="uv__code uv__code--mini">KOV</span> Hlavná 12, Košice</b></div>
            <div><span>Menu</span><Jedla polozky={[["2x", "2"], ["1x", "1"]]} /></div>
            <div><span>Cena objednávky</span><b>6,90 €</b></div>
            <div><span>Saldo zákazníka</span><b className="uv__saldo--dlh">12,40 € (Dlh)</b></div>
          </div>
          <label className="uv__label">Druh platby</label>
          <div className="uv__input uv__input--select">Hotovosť (CASH) <ChevronDown size={12} /></div>
          <label className="uv__label">Zaplatená suma</label>
          <div className={`uv__input ${po("suma") ? "uv__input--fokus" : ""}`}>
            {po("suma") ? "19.3" : "6.9"}
            {po("suma") && <i className="uv__caret" />}
          </div>
          {po("suma") ? (
            <div className="uv__feedback uv__feedback--ok">
              <CheckCircle2 size={13} />
              <span>Zákazník zaplatil o <b>12,40 €</b> navyše. Dlh zákazníka sa zníži o <b>12,40 €</b>.</span>
            </div>
          ) : (
            <div className="uv__feedback uv__feedback--ok">
              <CheckCircle2 size={13} />
              <span>Plná platba – bez zmeny salda zákazníka.</span>
            </div>
          )}
          <div className="uv__confirm">
            Potvrdiť doručenie a platbu
            <span className={`uv__tap ${po("potvrd") ? "uv__tap--tu" : ""}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function UkazkaVodic() {
  const rootRef = useRef(null);
  const { kolo, krok, po, poPre, skocNa } = useScenar(rootRef, KROKY, DLZKA_KOLA);

  // koniec kola aj skok na kapitolu: predosly zaber sa rozplynie do noveho
  const odchadza = usePrelinanie(kolo, krok);
  // na uzkej obrazovke je vidno bud mobil, alebo mapu -- mapu len pocas jazdy
  const mapaNaMobile = (po("navigacia") && !po("spat")) || po("dalsia");

  return (
    <div
      ref={rootRef}
      className={["uv", mapaNaMobile ? "uv--mapa" : ""].filter(Boolean).join(" ")}
    >
      <div className="uv__scena" aria-hidden="true">
        <div className="uv__phone">
          <div className="uv__screen">
            <div className="uv__statusbar">
              <span>9:41</span>
              <span className="uv__island" />
              <span className="uv__sys"><Signal size={10} /><Wifi size={10} /><BatteryFull size={12} /></span>
            </div>

            <MobilObsah key={kolo} po={po} />
            {odchadza && <MobilObsah key={`k${odchadza.kolo}`} po={poPre(odchadza.krok)} className="uv__stary" />}
            <div className="uv__home" />
          </div>
        </div>

        <div className="uv__map-wrap">
          <Mapa key={kolo} po={po} />
          {odchadza && <Mapa key={`k${odchadza.kolo}`} po={poPre(odchadza.krok)} staticky className="uv__stary" />}
        </div>
      </div>

      <Kapitoly kapitoly={KAPITOLY} po={po} skocNa={skocNa} />
    </div>
  );
}

export default UkazkaVodic;
